import * as React from 'react'
import PropTypes from 'prop-types'
import { dataProps } from '@ds/react-utils'
import { useThemeStyles } from '../../theming'
import { FlexLayoutChild } from './FlexLayoutChild'
import styles from './styles'

const flexLayoutAlignItems = ['start', 'center', 'end', 'stretch'] as const
const flexLayoutDirection = ['row', 'column'] as const
export const wrapValues = ['nowrap', 'wrap', 'wrap-reverse'] as const
export const alignContentValues = [
  'start',
  'center',
  'end',
  'space-between',
  'space-around',
  'space-evenly',
  'stretch',
] as const

export type FlexLayoutAlignItems = (typeof flexLayoutAlignItems)[number]
export type FlexLayoutDirection = (typeof flexLayoutDirection)[number]
export type FlexLayoutWrap = (typeof wrapValues)[number]
export type FlexLayoutContent = (typeof alignContentValues)[number]

export interface FlexLayoutProps {
  /**
   * The distribution of space between and around content items along a
   * flexbox's cross-axis.
   */
  alignContent?: FlexLayoutContent
  /**
   * The alignment of the FlexLayout children.
   */
  alignItems?: FlexLayoutAlignItems
  /**
   * The 'children' prop accepts arbitrary nodes, but the normal usage pattern is:
   * `<FlexLayout>` `<FlexLayout.Child />` `</FlexLayout />`
   */
  children: React.ReactNode
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * The layout direction of the FlexLayout children. Accepts the value of row or column.
   */
  direction?: FlexLayoutDirection
  /**
   * Whether flex items are forced onto one line or can wrap onto multiple lines.
   * If wrapping is allowed, it sets the direction that lines are stacked.
   */
  flexWrap?: FlexLayoutWrap
  /**
   * Defines how the browser distributes space between and around content items
   * along the main-axis of a flex container.
   */
  justifyContent?: FlexLayoutContent
}

export function FlexLayout({
  alignContent,
  alignItems = 'start',
  children,
  direction = 'row',
  flexWrap = 'nowrap',
  justifyContent,
  ...restProps
}: FlexLayoutProps) {
  const sx = useThemeStyles(styles, {
    alignContent,
    alignItems,
    direction,
    flexWrap,
    justifyContent,
  })

  return (
    <div {...dataProps(restProps)} css={sx.default.wrap}>
      {children}
    </div>
  )
}

FlexLayout.alignItemsValues = flexLayoutAlignItems
FlexLayout.directionValues = flexLayoutDirection

FlexLayout.propTypes = {
  alignContent: PropTypes.oneOf(alignContentValues),
  alignItems: PropTypes.oneOf(flexLayoutAlignItems),
  children: PropTypes.node.isRequired,
  direction: PropTypes.oneOf(flexLayoutDirection),
  flexWrap: PropTypes.oneOf(wrapValues),
  justifyContent: PropTypes.oneOf(alignContentValues),
}

FlexLayout.displayName = 'FlexLayout'

FlexLayout.Child = FlexLayoutChild
